import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent, SectionTagDefinition } from '~/shared/data-contract/job-profile';

import { DescriptionMarkStates } from '../../comments/DescriptionMarkStates';
import { Section, SectionHeader } from '../components/Common';
import { DescriptionSectionSkeleton } from '../components/SkeletonLoaders';
import { DescriptionSection, EditableDescriptionSection } from './EditableDescription';
import { structuredTaskSectionIds } from './structured-task-elements';
import type { ProfileForm } from './types';
import { useTranslatedSectionTitle } from './use-translated-section-title';

type StructuredContentSectionProps = {
    readonly activeLanguageIndex: number;
    readonly afterTasksContent?: ReactNode;
    readonly commentable: boolean;
    readonly control: Control<ProfileForm>;
    readonly isEditing: boolean;
    readonly isGeneratingTranslations: boolean;
    readonly itemId: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onUserInput: () => void;
    readonly overrideSectionTags?: readonly SectionTagDefinition[];
    readonly profile: Pick<JobProfileComponent, 'sectionTags' | 'structured' | 'translations'>;
};

export const StructuredContentSection: FunctionComponent<StructuredContentSectionProps> = ({
    activeLanguageIndex,
    afterTasksContent,
    commentable,
    control,
    isEditing,
    isGeneratingTranslations,
    itemId,
    newThreadCreationDisabled = false,
    onUserInput,
    overrideSectionTags,
    profile,
}) => {
    const translate = useTranslate();
    const sectionTags = overrideSectionTags ?? profile.sectionTags;
    const resolveTitle = useTranslatedSectionTitle(sectionTags);
    const watchedStructured = useWatch({ control, name: `translations.${activeLanguageIndex}.structured` });

    if (profile.structured === null) {
        return null;
    }

    // The backend synthesizes task elements (and an empty placeholder while no tasks exist) into
    // TASKS-type template sections; the structured tasks section (afterTasksContent) replaces them
    // at their template position, so they never render as text.
    const taskSectionIds = structuredTaskSectionIds(sectionTags);
    const isStructuredTaskElement = (item: { readonly id?: string }): boolean =>
        item.id !== undefined && taskSectionIds.has(item.id);

    const originalStructuredItems = profile.translations[activeLanguageIndex]?.structured ?? profile.structured ?? [];
    const displayedStructuredItems = isEditing
        ? (watchedStructured ?? profile.translations[activeLanguageIndex]?.structured)
        : profile.translations[activeLanguageIndex]?.structured;
    const firstTaskElementIndex = displayedStructuredItems?.findIndex(isStructuredTaskElement) ?? -1;
    const legacyTasksContentIndex =
        displayedStructuredItems?.findIndex((item) => item.tag === 'tasks' && !isStructuredTaskElement(item)) ?? -1;
    const tasksAnchorIndex = firstTaskElementIndex === -1 ? legacyTasksContentIndex : firstTaskElementIndex;

    return (
        <Section className="text-body-s" data-testid="structured-content-section">
            {commentable ? <DescriptionMarkStates itemId={itemId} /> : null}
            <SectionHeader>{translate('pages.job-architecture-v2.job-profile-title')}</SectionHeader>
            {isGeneratingTranslations && is.falsy(profile.translations?.[activeLanguageIndex]?.structured) ? (
                <Fragment>
                    <DescriptionSectionSkeleton />
                    <DescriptionSectionSkeleton />
                    <DescriptionSectionSkeleton />
                </Fragment>
            ) : (
                displayedStructuredItems?.map((structuredItem, index) => {
                    const tasksSlot = index === tasksAnchorIndex ? afterTasksContent : null;
                    if (isStructuredTaskElement(structuredItem)) {
                        return <Fragment key={structuredItem.taskId ?? `task-element-${index}`}>{tasksSlot}</Fragment>;
                    }
                    const displayTitle = resolveTitle(structuredItem);
                    const sectionKey = structuredItem.id ?? structuredItem.tag;
                    const originalStructuredItem = originalStructuredItems.find(
                        (item) => (item.id ?? item.tag) === sectionKey,
                    );
                    const commentMarkedHtml =
                        commentable && newThreadCreationDisabled ? originalStructuredItem?.value : undefined;
                    return (
                        <Fragment key={sectionKey}>
                            <div>
                                {isEditing ? (
                                    <EditableDescriptionSection
                                        key={activeLanguageIndex}
                                        activeLanguageIndex={activeLanguageIndex}
                                        commentable={commentable}
                                        commentMarkedHtml={commentMarkedHtml}
                                        control={control}
                                        index={index}
                                        isGenerating={false}
                                        itemId={itemId}
                                        sectionKey={sectionKey}
                                        sectionTitle={displayTitle}
                                        onUserInput={onUserInput}
                                    />
                                ) : (
                                    <DescriptionSection
                                        commentable={commentable}
                                        itemId={itemId}
                                        sectionKey={sectionKey}
                                        title={displayTitle}
                                        value={structuredItem.value}
                                    />
                                )}
                            </div>
                            {tasksSlot}
                        </Fragment>
                    );
                })
            )}
        </Section>
    );
};
